import React, { type ClipboardEvent, useCallback, useState } from 'react'
import { Autocomplete } from '@mui/material'

import { Chip, TextField, Typography } from '~components/internal'

import './ListInput.scss'

export interface ListInputProps {
  label?: string
  placeholder?: string
  validateItem?: (item: string) => boolean
  helperText?: string
  errorText?: string
  items: string[]
  setItems: (items: string[]) => void
  allowSentences?: boolean
  disabled?: boolean
  required?: boolean
  id?: string
}

const ListInput: React.FC<ListInputProps> = ({
  label,
  placeholder = 'Type an item and press Enter, or paste multiple items',
  validateItem = (): boolean => true,
  helperText,
  errorText,
  items,
  setItems,
  allowSentences = false,
  disabled = false,
  required = false,
  id,
}) => {
  const [inputValue, setInputValue] = useState('')

  const processInput = useCallback(
    (input: string): string[] => {
      if (allowSentences) {
        return input
          .split(/[\n,]+/)
          .map((item) => item.trim())
          .filter(Boolean)
      }
      return input
        .split(/[\n,\s]+/)
        .map((item) => item.trim())
        .filter(Boolean)
    },
    [allowSentences],
  )

  const addNewItems = useCallback(
    (newItems: string[]): void => {
      const uniqueItems = Array.from(new Set([...items, ...newItems]))
      setItems(uniqueItems)
    },
    [items, setItems],
  )

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>): void => {
    event.preventDefault()
    const pastedText = event.clipboardData.getData('text')
    const newItems = processInput(pastedText)
    addNewItems(newItems)
  }

  const handleInputChange = (_event: React.SyntheticEvent, value: string, reason: string): void => {
    if (reason === 'reset') {
      setInputValue('')
      return
    }

    if ((!allowSentences && value.endsWith(' ')) || value.endsWith(',')) {
      const newItems = processInput(value)
      addNewItems(newItems)
      setInputValue('')
    } else {
      setInputValue(value)
    }
  }

  const handleBlur = (): void => {
    if (inputValue.trim()) {
      const newItems = processInput(inputValue)
      addNewItems(newItems)
      setInputValue('')
    }
  }

  const hasError = !!errorText

  return (
    <div className='internal-list-input'>
      <Autocomplete<string, true, false, true>
        multiple
        freeSolo
        options={[] as string[]}
        value={items}
        onChange={(_event, newValue) => setItems(newValue)}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onBlur={handleBlur}
        disabled={disabled}
        renderValue={(value: string[], getItemProps) =>
          value.map((option: string, index: number) => (
            <Chip
              {...getItemProps({ index })}
              label={option}
              size='small'
              variant='outlined'
              color={validateItem(option) ? 'neutral' : 'red'}
            />
          ))
        }
        renderInput={({ slotProps, ...params }) => (
          <TextField
            {...params}
            InputProps={{
              ...slotProps.input,
            }}
            inputProps={slotProps.htmlInput}
            label={label}
            required={required}
            placeholder={placeholder}
            error={hasError}
            errorText={errorText}
            onPaste={handlePaste}
            id={id}
          />
        )}
      />
      {!hasError && helperText && (
        <div className='internal-list-input__helper-text'>
          <Typography variant='caption'>{helperText}</Typography>
        </div>
      )}
    </div>
  )
}

export default ListInput
